import { useQueryClient } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { useAuth } from '@clerk/clerk-react'
import { useLayoutEffect, useMemo, useRef } from 'react'
import { router } from './router'
import type { RouterContext } from './router'
import { setApiActorId, setTokenGetter } from './api/axios-instance'
import { PageLoader } from './components/ui/page-loader'
import { isIntentionalSignOutInProgress } from './hooks/use-intentional-sign-out'
import { clearOrgResolveStorage } from './lib/org-resolve-storage'
import { clearScopedQueryClients } from './providers/scoped-query-client-cache'

export function InnerApp() {
  const { isSignedIn, isLoaded, userId, orgId, getToken } = useAuth()

  const queryClient = useQueryClient()
  const previousActor = useRef(userId)

  // Clerk can drop a still-valid session from memory when a tab sits idle for
  // hours or the computer sleeps (its token refresh fails while offline). Trusting
  // that would bounce the user to /login although a fresh page load signs them
  // straight back in. So on a signed-in → signed-out flip, hard-reload: Clerk then
  // re-reads the real session from its server, and a genuine sign-out still ends
  // on /login (which returns the user to this page after they sign in). A sign-out
  // the user clicked is left alone so Clerk's redirect to /login goes through.
  const sessionDropped =
    isLoaded && !userId && Boolean(previousActor.current) && !isIntentionalSignOutInProgress()

  // Initialize axios token getter for API calls
  useLayoutEffect(() => {
    if (sessionDropped) {
      window.location.reload()
      return
    }
    setApiActorId(userId ?? null)
    if (previousActor.current !== userId) {
      void queryClient.cancelQueries()
      queryClient.clear()
      // The salon caches kept for quick salon hops go too: nothing of this person survives for the next.
      clearScopedQueryClients()
      // Signed out or another account: the next person starts without this person's saved salon addresses.
      if (previousActor.current) clearOrgResolveStorage()
      previousActor.current = userId
    }
    if (isLoaded && getToken) {
      setTokenGetter(getToken)
    }
  }, [isLoaded, getToken, userId, queryClient, sessionDropped])

  // Keep the router context referentially stable across renders. Clerk refreshes
  // its session token roughly every minute (and on tab focus), which re-renders
  // this component; without memoization every refresh hands the router a new
  // context object and forces every matched route's `beforeLoad` to re-run —
  // including the org-resolve fetch. Memoizing on the primitive auth fields means
  // the context only changes when auth actually changes.
  const context: RouterContext = useMemo(
    () => ({
      auth: {
        isSignedIn: isSignedIn ?? false,
        isLoaded,
        userId,
        orgId,
        orgName: null, // Will be fetched in org layout
      },
    }),
    [isSignedIn, isLoaded, userId, orgId]
  )

  // Wait for Clerk to load before rendering routes; hold the loader while a
  // dropped session reloads so routes never redirect off the current page.
  if (!isLoaded || sessionDropped) {
    return <PageLoader />
  }

  return <RouterProvider key={userId ?? 'signed-out'} router={router} context={context} />
}
